import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  validateUrl,
  extractYouTubeVideoId,
  validateContentDuration,
} from "../utils/validation.jsx";
import ConfirmationModal from "../components/ConfirmationModal.jsx";

// 서버 데이터 타입을 프론트엔드 타입으로 변환
const mapServerTypeToFrontend = (serverType) => {
  if (!serverType) return "";

  const typeMap = {
    IMAGE: "image",
    VIDEO: "video",
    WEBSITE: "website",
  };

  const typeString =
    typeof serverType === "string" ? serverType : serverType.toString();
  return typeMap[typeString] || typeString.toLowerCase();
};

// 프론트엔드 타입을 서버 타입으로 변환
const mapFrontendTypeToServer = (frontendType) => {
  const typeMap = {
    image: "IMAGE",
    video: "VIDEO",
    website: "WEBSITE",
  };
  return typeMap[frontendType] || frontendType;
};

// URL 타입 구분 헬퍼 함수
const isUploadedFileUrl = (url) => {
  return url && url.startsWith("blob:");
};

const isExternalUrl = (url) => {
  return url && !url.startsWith("blob:");
};

// 개선된 바이너리 데이터를 URL로 변환하는 함수
const convertBinaryDataToUrl = (binaryData, contentType = "image/jpeg") => {
  if (!binaryData) {
    return null;
  }

  try {
    let uint8Array;

    if (binaryData instanceof Uint8Array) {
      uint8Array = binaryData;
    } else if (binaryData instanceof ArrayBuffer) {
      uint8Array = new Uint8Array(binaryData);
    } else if (Array.isArray(binaryData)) {
      if (
        binaryData.some(
          (byte) => typeof byte !== "number" || byte < 0 || byte > 255
        )
      ) {
        return null;
      }
      uint8Array = new Uint8Array(binaryData);
    } else if (typeof binaryData === "string") {
      try {
        const binaryString = atob(binaryData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        uint8Array = bytes;
      } catch (e) {
        return null;
      }
    } else {
      return null;
    }

    if (uint8Array.length === 0) {
      return null;
    }

    const blob = new Blob([uint8Array], { type: contentType });
    if (blob.size === 0) {
      return null;
    }

    const objectUrl = URL.createObjectURL(blob);

    return objectUrl;
  } catch (error) {
    return null;
  }
};

// 파일 확장자에서 MIME 타입 추론
const getMimeTypeFromFilename = (fileName, defaultType = "image/jpeg") => {
  if (!fileName) return defaultType;

  const ext = fileName.toLowerCase().split(".").pop();
  const mimeMap = {
    // 이미지
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    svg: "image/svg+xml",
    tiff: "image/tiff",
    // 비디오
    mp4: "video/mp4",
    webm: "video/webm",
    ogg: "video/ogg",
    avi: "video/x-msvideo",
    mov: "video/quicktime",
    mkv: "video/x-matroska",
    wmv: "video/x-ms-wmv",
  };

  return mimeMap[ext] || defaultType;
};

// 섬네일 렌더링 컴포넌트
const ContentThumbnail = ({
  content,
  contentType,
  generateUrlForExistingContent,
  mappingId,
}) => {
  const [thumbnailError, setThumbnailError] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);

  useEffect(() => {
    setThumbnailError(false);

    if (contentType === "image") {
      // 이미지의 경우 직접 URL 사용
      let imageUrl = content?.url;

      if (!imageUrl && content?.binaryData) {
        imageUrl = generateUrlForExistingContent(
          content,
          content?.id || mappingId
        );
      }

      setThumbnailUrl(imageUrl);
    } else if (contentType === "video" && content?.url) {
      // YouTube 동영상의 경우 썸네일 추출
      const videoId = extractYouTubeVideoId(content.url);
      if (videoId) {
        setThumbnailUrl(`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`);
      } else {
        // 업로드된 동영상의 경우 현재는 썸네일 추출이 어려우므로 아이콘 사용
        setThumbnailError(true);
      }
    } else {
      setThumbnailError(true);
    }
  }, [content, contentType, generateUrlForExistingContent, mappingId]);

  const iconStyle = {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #74b9ff, #0984e3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    flexShrink: 0,
    overflow: "hidden",
  };

  const thumbnailStyle = {
    ...iconStyle,
    background: "transparent",
    border: "2px solid #e9ecef",
  };

  // 웹사이트는 항상 아이콘
  if (contentType === "website") {
    return <div style={iconStyle}>🌐</div>;
  }

  // 섬네일 에러가 있거나 URL이 없으면 아이콘 표시
  if (thumbnailError || !thumbnailUrl) {
    return (
      <div style={iconStyle}>
        {contentType === "image" && "🖼️"}
        {contentType === "video" && "🎬"}
        {!contentType && "📄"}
      </div>
    );
  }

  // 섬네일 표시
  return (
    <div style={thumbnailStyle}>
      <img
        src={thumbnailUrl}
        alt="섬네일"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: "6px",
        }}
        onError={() => setThumbnailError(true)}
        onLoad={(e) => {
          // 이미지가 너무 작거나 깨진 경우 에러 처리
          if (e.target.naturalWidth < 10 || e.target.naturalHeight < 10) {
            setThumbnailError(true);
          }
        }}
      />
      {/* 타입 표시 오버레이 */}
      <div
        style={{
          position: "absolute",
          bottom: "2px",
          right: "2px",
          background: "rgba(0,0,0,0.7)",
          color: "white",
          borderRadius: "3px",
          padding: "1px 3px",
          fontSize: "8px",
          lineHeight: "1",
        }}
      >
        {contentType === "image" && "🖼️"}
        {contentType === "video" && "🎬"}
      </div>
    </div>
  );
};

// 수정된 드래그 가능한 컨텐츠 아이템 컴포넌트
const SortableContentItem = ({
  mapping,
  index,
  onEdit,
  onRemove,
  generateUrlForExistingContent,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mapping.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const content = mapping.content;
  const contentType = mapServerTypeToFrontend(
    content?.contentType || content?.type
  );
  const displayTitle = mapping.name || content?.title || "제목 없음";

  return (
    <div ref={setNodeRef} style={style} className="sortable-content-item">
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "16px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: isDragging ? "2px dashed #74b9ff" : "1px solid #dee2e6",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* 드래그 핸들 */}
          <div
            {...attributes}
            {...listeners}
            style={{
              width: "20px",
              height: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#636e72",
              fontSize: "14px",
              cursor: isDragging ? "grabbing" : "grab",
              padding: "4px",
              borderRadius: "4px",
              transition: "background-color 0.2s",
              flexShrink: 0,
            }}
            onMouseOver={(e) => {
              if (!isDragging) {
                e.target.style.backgroundColor = "#f1f2f6";
              }
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "transparent";
            }}
          >
            ⋮⋮
          </div>

          {/* 순서 번호 */}
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              background: "linear-gradient(45deg, #74b9ff, #0984e3)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              flexShrink: 0,
            }}
          >
            {index + 1}
          </div>

          {/* 섬네일 또는 아이콘 */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <ContentThumbnail
              content={content}
              contentType={contentType}
              generateUrlForExistingContent={generateUrlForExistingContent}
              mappingId={mapping.id}
            />
          </div>

          {/* 컨텐츠 정보 */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              textAlign: "left",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#2d3436",
                marginBottom: "4px",
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayTitle}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#636e72",
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {contentType === "image" &&
                `이미지 | ${mapping.playDuration || 10}초`}
              {contentType === "video" &&
                `${
                  content?.videoType === "youtube" ? "YouTube" : "업로드"
                } 동영상 | ${mapping.playDuration || 10}초`}
              {contentType === "website" &&
                `웹사이트 | ${mapping.playDuration || 10}초`}
              {!contentType && `재생시간: ${mapping.playDuration || 10}초`}
              {mapping.isMusicPlay && " | 음악 재생"}
            </div>
          </div>
        </div>

        {/* 버튼 그룹 */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            flexShrink: 0,
            marginLeft: "12px",
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(mapping, index);
            }}
            style={{
              background: "linear-gradient(45deg, #74b9ff, #0984e3)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            수정
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove(mapping);
            }}
            style={{
              background: "linear-gradient(45deg, #e17055, #d63031)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            삭제
          </button>
        </div>
      </div>
    </div>
  );
};

// 수정된 ContentManagement - URL 타입 기반 분석 적용
const ContentManagement = ({
  tvId,
  contents,
  onRefresh,
  onContentsChange,
  toastSuccess,
  toastError,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState("");
  const [contentForm, setContentForm] = useState({
    title: "",
    type: "",
    url: "",
    duration: 10,
    imageType: "upload",
    videoType: "youtube",
    youtubeVisibility: "unlisted", // "public" (전체공개) 또는 "unlisted" (일부공개)
    enableVideoMusic: false,
    file: null,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [isUploading, setIsUploading] = useState(false);
  const [localContentList, setLocalContentList] = useState([]);

  // 파일 변경 관리 및 미리보기
  const [currentFileInfo, setCurrentFileInfo] = useState(null);
  const [isFileChanged, setIsFileChanged] = useState(false);
  const [fileChangeMessage, setFileChangeMessage] = useState("");

  // 미리보기 URL 관리 개선
  const [newFilePreviewUrl, setNewFilePreviewUrl] = useState(null);
  const [existingContentPreviewUrls, setExistingContentPreviewUrls] = useState(
    new Map()
  );

  // 삭제 확인 모달 상태
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);

  // 서버 데이터 컨텐츠 URL 캐시
  const [serverContentUrlCache, setServerContentUrlCache] = useState(new Map());
  const urlCacheRef = useRef(new Set());

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 기존 컨텐츠 URL 생성 함수
  const generateUrlForExistingContent = useCallback(
    (contentData, contentId) => {
      if (!contentData || !contentData.binaryData) return null;

      const cacheKey = `existing_${contentId}_${
        contentData.fileName || "unknown"
      }`;

      if (existingContentPreviewUrls.has(cacheKey)) {
        return existingContentPreviewUrls.get(cacheKey);
      }

      const mimeType =
        contentData.mimeType ||
        contentData.mineType ||
        getMimeTypeFromFilename(contentData.fileName, "image/jpeg");

      const generatedUrl = convertBinaryDataToUrl(
        contentData.binaryData,
        mimeType
      );

      if (generatedUrl) {
        setExistingContentPreviewUrls(
          (prev) => new Map(prev.set(cacheKey, generatedUrl))
        );
        urlCacheRef.current.add(generatedUrl);

        return generatedUrl;
      }

      return null;
    },
    [existingContentPreviewUrls]
  );

  // 개선된 서버 데이터에서 컨텐츠 URL 처리
  const getServerContentUrl = useCallback(
    (contentData) => {
      if (!contentData) return null;

      const contentType = mapServerTypeToFrontend(
        contentData.contentType || contentData.type
      );
      const cacheKey = `${contentData.id}_${contentType}_${
        contentData.fileName || "unknown"
      }`;

      if (serverContentUrlCache.has(cacheKey)) {
        const cachedUrl = serverContentUrlCache.get(cacheKey);
        return cachedUrl;
      }

      let contentUrl = null;

      if (contentData.url) {
        contentUrl = contentData.url;
      } else if (contentData.binaryData) {
        let mimeType = contentData.mimeType || contentData.mineType;

        if (!mimeType) {
          mimeType = getMimeTypeFromFilename(
            contentData.fileName,
            contentType === "image" ? "image/jpeg" : "video/mp4"
          );
        }

        contentUrl = convertBinaryDataToUrl(contentData.binaryData, mimeType);

        if (contentUrl) {
          urlCacheRef.current.add(contentUrl);
        }
      }

      if (contentUrl) {
        setServerContentUrlCache(
          (prev) => new Map(prev.set(cacheKey, contentUrl))
        );
      }

      return contentUrl;
    },
    [serverContentUrlCache]
  );

  // contents가 변경될 때 로컬 상태 업데이트 및 서버 데이터 URL 처리
  useEffect(() => {
    if (contents) {
      const processedContents = contents.map((item) => {
        if (item.content) {
          let processedUrl = item.content.url;

          if (!processedUrl && item.content.binaryData) {
            processedUrl = getServerContentUrl(item.content);
          }

          return {
            ...item,
            content: {
              ...item.content,
              url: processedUrl,
              type: mapServerTypeToFrontend(item.content.contentType),
            },
          };
        }
        return item;
      });

      setLocalContentList([...processedContents]);
    }
  }, [contents, getServerContentUrl]);

  // 메모리 정리 개선
  useEffect(() => {
    return () => {
      if (newFilePreviewUrl) {
        URL.revokeObjectURL(newFilePreviewUrl);
      }

      existingContentPreviewUrls.forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      urlCacheRef.current.forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      urlCacheRef.current.clear();

      serverContentUrlCache.forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  // 로컬 상태 변경을 부모 컴포넌트에 알림
  const notifyContentsChange = (newContentList) => {
    setLocalContentList(newContentList);
    if (onContentsChange && typeof onContentsChange === "function") {
      onContentsChange(newContentList);
    }
  };

  const resetForm = () => {
    setContentForm({
      title: "",
      type: "",
      url: "",
      duration: 10,
      imageType: "upload",
      videoType: "youtube",
      youtubeVisibility: "unlisted",
      enableVideoMusic: false,
      file: null,
    });
    setSelectedContentType("");
    setIsEditMode(false);
    setEditingIndex(-1);
    setCurrentFileInfo(null);
    setIsFileChanged(false);
    setFileChangeMessage("");

    if (newFilePreviewUrl) {
      URL.revokeObjectURL(newFilePreviewUrl);
      setNewFilePreviewUrl(null);
    }
  };

  const handleAddContent = () => {
    setShowAddModal(true);
    resetForm();
  };

  const handleEditContent = (mapping, index) => {
    const content = mapping.content;
    const contentType = mapServerTypeToFrontend(
      content?.contentType || content?.type
    );

    // 이미지 타입 판단
    let imageType = "upload";
    if (contentType === "image") {
      if (isUploadedFileUrl(content?.url)) {
        imageType = "upload";
      } else {
        imageType = "url";
      }
    }

    // URL 타입으로 동영상 타입 판단
    let videoType = "youtube";
    if (contentType === "video") {
      if (isUploadedFileUrl(content?.url)) {
        videoType = "upload";
      } else {
        videoType = "youtube";
      }
    }

    setContentForm({
      title: mapping.name || content?.title || "",
      type: contentType,
      url: content?.url || "",
      duration: mapping.playDuration || 10,
      imageType: imageType,
      videoType: videoType,
      youtubeVisibility: content?.youtubeVisibility || "unlisted",
      enableVideoMusic: mapping.isMusicPlay || false,
      file: null,
    });
    setSelectedContentType(contentType);
    setIsEditMode(true);
    setEditingIndex(index);

    if (
      (contentType === "image" || contentType === "video") &&
      isUploadedFileUrl(content?.url)
    ) {
      setCurrentFileInfo({
        fileName: content.fileName || "알 수 없는 파일",
        title: mapping.name || content.title || "제목 없음",
        type: contentType,
        isUploadedFile: true,
      });
    } else {
      setCurrentFileInfo(null);
    }

    setIsFileChanged(false);
    setFileChangeMessage("");
    setShowAddModal(true);
  };

  // 삭제 관련 핸들러들
  const handleDeleteContent = (mapping) => {
    setContentToDelete(mapping);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!contentToDelete) return;

    const content = contentToDelete.content;
    const newContentList = localContentList.filter(
      (item) => item.id !== contentToDelete.id
    );
    notifyContentsChange(newContentList);

    if (toastSuccess) {
      toastSuccess(`"${content?.title || "컨텐츠"}"가 삭제되었습니다.`);
    }

    setShowDeleteModal(false);
    setContentToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setContentToDelete(null);
  };

  // 드래그 앤 드롭 핸들러
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const newContentList = [...localContentList];
      const oldIndex = newContentList.findIndex(
        (item) => item.id.toString() === active.id
      );
      const newIndex = newContentList.findIndex(
        (item) => item.id.toString() === over.id
      );

      const reorderedList = arrayMove(newContentList, oldIndex, newIndex);
      notifyContentsChange(reorderedList);
    }
  };

  // 파일 검증 함수들
  const validateImageFileImproved = (file) => {
    if (!file) {
      return { valid: false, message: "파일을 선택해주세요." };
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { valid: false, message: "파일 크기는 10MB 이하여야 합니다." };
    }

    const supportedMimeTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/bmp",
      "image/svg+xml",
    ];
    const supportedExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".webp",
      ".bmp",
      ".svg",
    ];

    const mimeTypeValid = supportedMimeTypes.includes(file.type.toLowerCase());
    const fileName = file.name.toLowerCase();
    const extensionValid = supportedExtensions.some((ext) =>
      fileName.endsWith(ext)
    );

    if (!mimeTypeValid && !extensionValid) {
      return {
        valid: false,
        message:
          "지원하지 않는 이미지 형식입니다. (JPG, PNG, GIF, WebP, BMP, SVG만 지원)",
      };
    }

    return { valid: true };
  };

  const validateVideoFileImproved = (file) => {
    if (!file) {
      return { valid: false, message: "파일을 선택해주세요." };
    }

    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return { valid: false, message: "파일 크기는 50MB 이하여야 합니다." };
    }

    const supportedMimeTypes = [
      "video/mp4",
      "video/webm",
      "video/ogg",
      "video/avi",
      "video/x-msvideo",
      "video/quicktime",
      "video/mov",
    ];
    const supportedExtensions = [".mp4", ".webm", ".ogg", ".avi", ".mov"];

    const mimeTypeValid = supportedMimeTypes.includes(file.type.toLowerCase());
    const fileName = file.name.toLowerCase();
    const extensionValid = supportedExtensions.some((ext) =>
      fileName.endsWith(ext)
    );

    if (!mimeTypeValid && !extensionValid) {
      return {
        valid: false,
        message:
          "지원하지 않는 비디오 형식입니다. (MP4, WebM, OGG, AVI, MOV만 지원)",
      };
    }

    return { valid: true };
  };

  // 간단화된 파일 업로드 처리
  const handleFileUpload = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const arrayBuffer = event.target.result;
        const uint8Array = new Uint8Array(arrayBuffer);

        const blob = new Blob([uint8Array], { type: file.type });
        const directUrl = URL.createObjectURL(blob);

        resolve({
          binaryData: Array.from(uint8Array),
          url: directUrl,
          fileName: file.name,
          fileType: file.type,
          mimeType: file.type,
        });
      };
      reader.onerror = () => {
        reject(new Error("파일을 읽는 중 오류가 발생했습니다."));
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // 개선된 파일 선택 핸들러
  const handleFileSelect = useCallback(
    (file, fileType) => {
      setContentForm((prev) => ({ ...prev, file }));

      if (newFilePreviewUrl) {
        URL.revokeObjectURL(newFilePreviewUrl);
        setNewFilePreviewUrl(null);
      }

      if (isEditMode) {
        if (file) {
          setIsFileChanged(true);
          setFileChangeMessage(`새 파일: ${file.name}`);
        } else {
          setIsFileChanged(false);
          setFileChangeMessage("");
        }
      }

      if (file) {
        try {
          const immediatePreviewUrl = URL.createObjectURL(file);
          setNewFilePreviewUrl(immediatePreviewUrl);
          urlCacheRef.current.add(immediatePreviewUrl);
        } catch (error) {
          // 오류 처리
        }
      }
    },
    [newFilePreviewUrl, isEditMode]
  );

  // 통합된 미리보기 렌더링 함수
  const renderUnifiedPreview = useCallback(() => {
    const previewStyle = {
      width: "100%",
      height: "200px",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      marginBottom: "20px",
      position: "relative",
      border: "3px solid #e9ecef",
    };

    const placeholderStyle = {
      textAlign: "center",
      color: "white",
      fontSize: "16px",
      fontWeight: "600",
    };

    // 새로 선택한 파일의 미리보기 (최우선)
    if (newFilePreviewUrl && contentForm.file) {
      if (contentForm.file.type.startsWith("image/")) {
        return (
          <div style={previewStyle}>
            <img
              src={newFilePreviewUrl}
              alt="새 파일 미리보기"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "9px",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        );
      } else if (contentForm.file.type.startsWith("video/")) {
        return (
          <div style={previewStyle}>
            <video
              src={newFilePreviewUrl}
              controls
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "9px",
              }}
              onError={() => {
                // 비디오 로드 오류 처리
              }}
            />
          </div>
        );
      }
    }

    // 수정 모드에서 기존 컨텐츠의 미리보기
    if (isEditMode && !contentForm.file && editingIndex >= 0) {
      const currentMapping = localContentList[editingIndex];
      const currentContent = currentMapping?.content;

      if (currentContent) {
        // 이미지 미리보기
        if (currentContent.type === "image") {
          let imageUrl = currentContent.url;

          if (!imageUrl && currentContent.binaryData) {
            imageUrl = generateUrlForExistingContent(
              currentContent,
              currentContent.id || editingIndex
            );
          }

          if (imageUrl) {
            return (
              <div style={previewStyle}>
                <img
                  src={imageUrl}
                  alt="현재 이미지 미리보기"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: "9px",
                  }}
                  onError={(e) => {
                    const errorDiv = document.createElement("div");
                    errorDiv.style.cssText = `
                      width: 100%; height: 100%; 
                      display: flex; align-items: center; justify-content: center;
                      background: rgba(220, 53, 69, 0.1); color: #dc3545; 
                      font-size: 14px; text-align: center;
                    `;
                    errorDiv.innerHTML = `❌ 이미지 로드 실패<br/><small>${
                      currentContent.fileName || "Unknown file"
                    }</small>`;
                    e.target.parentNode.replaceChild(errorDiv, e.target);
                  }}
                />
              </div>
            );
          }
        }

        // 비디오 미리보기
        if (currentContent.type === "video") {
          if (contentForm.videoType === "youtube" && currentContent.url) {
            const videoId = extractYouTubeVideoId(currentContent.url);
            if (videoId) {
              return (
                <div style={previewStyle}>
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    style={{
                      borderRadius: "9px",
                      border: "none",
                    }}
                    title="YouTube 미리보기"
                  ></iframe>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "8px",
                      left: "8px",
                      background: "rgba(0,0,0,0.7)",
                      color: "white",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                  >
                    🎬 YouTube 동영상
                  </div>
                </div>
              );
            }
          } else if (contentForm.videoType === "upload" && currentContent.url) {
            let videoUrl = currentContent.url;

            if (!videoUrl && currentContent.binaryData) {
              videoUrl = generateUrlForExistingContent(
                currentContent,
                currentContent.id || editingIndex
              );
            }

            const isUploadedFile = isUploadedFileUrl(videoUrl);

            if (isUploadedFile && videoUrl) {
              return (
                <div style={previewStyle}>
                  <video
                    src={videoUrl}
                    controls
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: "9px",
                    }}
                    onError={(e) => {
                      const errorDiv = document.createElement("div");
                      errorDiv.style.cssText = `
                        width: 100%; height: 100%; 
                        display: flex; align-items: center; justify-content: center;
                        background: rgba(220, 53, 69, 0.1); color: #dc3545; 
                        font-size: 14px; text-align: center;
                      `;
                      errorDiv.innerHTML = `❌ 비디오 로드 실패<br/><small>${
                        currentContent.fileName || "Unknown file"
                      }</small>`;
                      e.target.parentNode.replaceChild(errorDiv, e.target);
                    }}
                  />
                </div>
              );
            }
          }
        }

        // 웹사이트 미리보기
        if (currentContent.type === "website" && currentContent.url) {
          return (
            <div style={previewStyle}>
              <iframe
                src={currentContent.url}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  borderRadius: "9px",
                  background: "white",
                }}
                title="웹사이트 미리보기"
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "8px",
                  left: "8px",
                  background: "rgba(0,0,0,0.7)",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                }}
              >
                🌐 현재 웹사이트: {currentContent.url}
              </div>
            </div>
          );
        }
      }
    }

    // 새 URL 입력에 따른 미리보기
    if (contentForm.url && !contentForm.file && validateUrl(contentForm.url)) {
      if (selectedContentType === "image" && contentForm.imageType === "url") {
        return (
          <div style={previewStyle}>
            <img
              src={contentForm.url}
              alt="URL 미리보기"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "9px",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: "8px",
                left: "8px",
                background: "rgba(0,0,0,0.7)",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
              }}
            >
              🔗 외부 이미지 URL
            </div>
          </div>
        );
      } else if (
        selectedContentType === "video" &&
        contentForm.videoType === "youtube"
      ) {
        const videoId = extractYouTubeVideoId(contentForm.url);
        if (videoId) {
          return (
            <div style={previewStyle}>
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${videoId}`}
                style={{
                  borderRadius: "9px",
                  border: "none",
                }}
                title="YouTube 미리보기"
              ></iframe>
              <div
                style={{
                  position: "absolute",
                  bottom: "8px",
                  left: "8px",
                  background: "rgba(0,0,0,0.7)",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                }}
              >
                🎬 YouTube 동영상
              </div>
            </div>
          );
        }
      } else if (selectedContentType === "website") {
        return (
          <div style={previewStyle}>
            <iframe
              src={contentForm.url}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: "9px",
                background: "white",
              }}
              title="웹사이트 미리보기"
            />
            <div
              style={{
                position: "absolute",
                bottom: "8px",
                left: "8px",
                background: "rgba(0,0,0,0.7)",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
              }}
            >
              🌐 웹사이트 미리보기
            </div>
          </div>
        );
      }
    }

    // 기본 플레이스홀더
    const getPlaceholderContent = () => {
      if (selectedContentType === "image") {
        return (
          <div style={placeholderStyle}>
            <div style={{ fontSize: "48px", marginBottom: "10px" }}>🖼️</div>
            <div>
              {contentForm.imageType === "upload"
                ? "이미지 파일을 선택해주세요"
                : "이미지 URL을 입력해주세요"}
            </div>
          </div>
        );
      } else if (selectedContentType === "video") {
        return (
          <div style={placeholderStyle}>
            <div style={{ fontSize: "48px", marginBottom: "10px" }}>🎬</div>
            <div>
              {contentForm.videoType === "youtube"
                ? "YouTube URL을 입력해주세요"
                : "비디오 파일을 선택해주세요"}
            </div>
          </div>
        );
      } else if (selectedContentType === "website") {
        return (
          <div style={placeholderStyle}>
            <div style={{ fontSize: "48px", marginBottom: "10px" }}>🌐</div>
            <div>웹사이트 URL을 입력해주세요</div>
          </div>
        );
      }
      return (
        <div style={placeholderStyle}>
          <div style={{ fontSize: "48px", marginBottom: "10px" }}>📋</div>
          <div>컨텐츠 타입을 선택해주세요</div>
        </div>
      );
    };

    return <div style={previewStyle}>{getPlaceholderContent()}</div>;
  }, [
    newFilePreviewUrl,
    contentForm.file,
    contentForm.url,
    contentForm.imageType,
    contentForm.videoType,
    selectedContentType,
    isEditMode,
    editingIndex,
    localContentList,
    generateUrlForExistingContent,
  ]);

  // 제출 핸들러
  const handleSubmit = async () => {
    if (!selectedContentType) {
      if (toastError) {
        toastError("컨텐츠 타입을 선택하세요.");
      }
      return;
    }

    if (!contentForm.title.trim()) {
      if (toastError) {
        toastError("제목을 입력하세요.");
      }
      return;
    }

    const durationValidation = validateContentDuration(contentForm.duration);
    if (!durationValidation.valid) {
      if (toastError) {
        toastError(durationValidation.message);
      }
      return;
    }

    setIsUploading(true);

    try {
      let contentData = {
        title: contentForm.title.trim(),
        type: selectedContentType,
        duration: parseInt(contentForm.duration),
        enableVideoMusic: contentForm.enableVideoMusic,
        url: null,
        fileName: null,
        videoType: null,
      };

      // 타입별 처리
      if (selectedContentType === "image") {
        if (isEditMode && !isFileChanged) {
          const originalMapping = localContentList[editingIndex];
          contentData.url = originalMapping.content?.url;
          contentData.fileName = originalMapping.content?.fileName;
          contentData.binaryData = originalMapping.content?.binaryData;
        } else {
          if (contentForm.imageType === "upload") {
            if (contentForm.file) {
              const validation = validateImageFileImproved(contentForm.file);
              if (!validation.valid) {
                if (toastError) {
                  toastError(validation.message);
                }
                return;
              }
              const fileData = await handleFileUpload(contentForm.file);
              contentData.url = fileData.url;
              contentData.fileName = fileData.fileName;
              contentData.binaryData = fileData.binaryData;
              contentData.mimeType = fileData.mimeType;

              urlCacheRef.current.add(fileData.url);
            } else {
              if (toastError) {
                toastError("이미지 파일을 선택하세요.");
              }
              return;
            }
          } else {
            if (contentForm.url) {
              if (!validateUrl(contentForm.url)) {
                if (toastError) {
                  toastError("유효한 이미지 URL을 입력하세요.");
                }
                return;
              }
              contentData.url = contentForm.url;
              contentData.binaryData = null;
            } else {
              if (toastError) {
                toastError("이미지 URL을 입력하세요.");
              }
              return;
            }
          }
        }
      } else if (selectedContentType === "video") {
        // YouTube 동영상만 지원
        if (!contentForm.url) {
          if (toastError) {
            toastError("YouTube URL을 입력하세요.");
          }
          return;
        }
        const videoId = extractYouTubeVideoId(contentForm.url);
        if (!videoId) {
          if (toastError) {
            toastError("유효한 YouTube URL을 입력하세요.");
          }
          return;
        }
        contentData.url = contentForm.url;
        contentData.videoType = "youtube";
        contentData.youtubeVisibility = contentForm.youtubeVisibility;
        contentData.binaryData = null;
      } else if (selectedContentType === "website") {
        if (!contentForm.url) {
          if (toastError) {
            toastError("웹사이트 URL을 입력하세요.");
          }
          return;
        }
        if (!validateUrl(contentForm.url)) {
          if (toastError) {
            toastError("유효한 웹사이트 URL을 입력하세요.");
          }
          return;
        }
        contentData.url = contentForm.url;
        contentData.binaryData = null;
      }

      // 브라우저 상태에만 추가/수정 (API 호출 없음)
      const newContentList = [...localContentList];

      if (isEditMode) {
        const contentMapping = {
          ...newContentList[editingIndex],
          name: contentData.title,
          playDuration: contentData.duration,
          isMusicPlay: contentData.enableVideoMusic || false,
          content: {
            id: newContentList[editingIndex].content?.id || null,
            title: contentData.title,
            type: contentData.type,
            contentType: mapFrontendTypeToServer(contentData.type),
            url: contentData.url,
            fileName: contentData.fileName,
            videoType: contentData.videoType,
            youtubeVisibility: contentData.youtubeVisibility || "unlisted",
            binaryData: contentData.binaryData || null,
            mimeType: contentData.mimeType || null,
          },
        };
        newContentList[editingIndex] = contentMapping;

        if (toastSuccess) {
          toastSuccess(`"${contentData.title}" 컨텐츠가 수정되었습니다.`);
        }
      } else {
        const newContentMapping = {
          id: Date.now() + Math.random(),
          mappingId: null,
          sortOrder: newContentList.length + 1,
          name: contentData.title,
          playDuration: contentData.duration,
          isMusicPlay: contentData.enableVideoMusic || false,
          content: {
            id: null,
            title: contentData.title,
            type: contentData.type,
            contentType: mapFrontendTypeToServer(contentData.type),
            url: contentData.url,
            fileName: contentData.fileName,
            videoType: contentData.videoType,
            youtubeVisibility: contentData.youtubeVisibility || "unlisted",
            binaryData: contentData.binaryData || null,
            mimeType: contentData.mimeType || null,
            isNewItem: true,
          },
        };
        newContentList.push(newContentMapping);

        if (toastSuccess) {
          toastSuccess(`"${contentData.title}" 컨텐츠가 추가되었습니다.`);
        }
      }

      notifyContentsChange(newContentList);

      setShowAddModal(false);
      resetForm();
    } catch (error) {
      if (toastError) {
        toastError(
          `컨텐츠 ${isEditMode ? "수정" : "추가"} 실패: ${error.message}`
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  const renderContentForm = () => {
    if (!selectedContentType) return null;

    return (
      <div>
        <label
          style={{
            display: "block",
            marginBottom: "5px",
            fontWeight: "bold",
            color: "#2d3436",
          }}
        >
          미리보기
        </label>
        {renderUnifiedPreview()}

        {/* 제목 입력 */}
        <div style={{ marginBottom: "15px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
              color: "#2d3436",
            }}
          >
            제목
          </label>
          <input
            type="text"
            value={contentForm.title}
            onChange={(e) =>
              setContentForm((prev) => ({ ...prev, title: e.target.value }))
            }
            placeholder="컨텐츠 제목 입력"
            style={{
              width: "100%",
              padding: "10px",
              border: "2px solid #ddd",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          />
        </div>

        {/* 표시 시간 입력 */}
        <div style={{ marginBottom: "15px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
              color: "#2d3436",
            }}
          >
            표시 시간 (초)
          </label>
          <input
            type="number"
            value={contentForm.duration}
            onChange={(e) =>
              setContentForm((prev) => ({ ...prev, duration: e.target.value }))
            }
            min="1"
            max="3600"
            style={{
              width: "100%",
              padding: "10px",
              border: "2px solid #ddd",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          />
        </div>

        {/* 유형별 첨부 UI */}
        {selectedContentType === "image" && (
          <>
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                  color: "#2d3436",
                }}
              >
                이미지 타입
              </label>
              <select
                value={contentForm.imageType}
                onChange={(e) =>
                  setContentForm((prev) => ({
                    ...prev,
                    imageType: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "2px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              >
                <option value="upload">파일 업로드</option>
                <option value="url">이미지 URL</option>
              </select>
            </div>

            {isEditMode &&
              currentFileInfo &&
              currentFileInfo.type === "image" &&
              contentForm.imageType === "upload" && (
                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontWeight: "bold",
                      color: "#2d3436",
                    }}
                  >
                    현재 이미지 정보
                  </label>
                  <div
                    style={{
                      background: "#f8f9fa",
                      border: "1px solid #dee2e6",
                      borderRadius: "6px",
                      padding: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span style={{ fontSize: "18px" }}>🖼️</span>
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#2d3436",
                        }}
                      >
                        {currentFileInfo.title}
                      </div>
                    </div>
                  </div>

                  {isFileChanged && (
                    <div
                      style={{
                        background: "#e3f2fd",
                        border: "1px solid #2196f3",
                        borderRadius: "6px",
                        padding: "10px",
                        marginTop: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span style={{ fontSize: "16px" }}>✅</span>
                      <div style={{ fontSize: "14px", color: "#1976d2" }}>
                        <strong>파일이 변경되었습니다!</strong>
                        <br />
                        <span style={{ fontSize: "12px" }}>
                          {fileChangeMessage}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

            {contentForm.imageType === "upload" ? (
              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    color: "#2d3436",
                  }}
                >
                  {isEditMode ? "변경할 이미지 파일" : "이미지 파일"}
                </label>
                <input
                  id="modalImageFile"
                  type="file"
                  accept="image/*,.jpg,.jpeg,.png,.gif,.webp,.bmp,.svg"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    handleFileSelect(file, "이미지");
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "2px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
                <small
                  style={{
                    color: "#666",
                    fontSize: "12px",
                    marginTop: "5px",
                    display: "block",
                  }}
                >
                  지원 형식: JPG, PNG, GIF, WebP, BMP, SVG (최대 10MB)
                  {isEditMode && (
                    <span
                      style={{
                        color: "#e17055",
                        display: "block",
                        marginTop: "3px",
                      }}
                    >
                      • 새 파일을 선택하지 않으면 기존 파일이 유지됩니다
                    </span>
                  )}
                </small>
              </div>
            ) : (
              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    color: "#2d3436",
                  }}
                >
                  이미지 URL
                </label>
                <input
                  type="url"
                  value={contentForm.url}
                  onChange={(e) =>
                    setContentForm((prev) => ({
                      ...prev,
                      url: e.target.value,
                    }))
                  }
                  placeholder="https://example.com/image.jpg"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "2px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
            )}
          </>
        )}

        {selectedContentType === "video" && (
          <>
            {/* YouTube 동영상만 지원 */}
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                  color: "#2d3436",
                }}
              >
                동영상 타입
              </label>
              <div
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "2px solid #e3f2fd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  background: "#e3f2fd",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "16px" }}>🎬</span>
                <span style={{ color: "#1976d2", fontWeight: "500" }}>
                  YouTube 동영상만 지원됩니다
                </span>
              </div>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                  color: "#2d3436",
                }}
              >
                YouTube URL
              </label>
              <input
                type="url"
                value={contentForm.url}
                onChange={(e) =>
                  setContentForm((prev) => ({ ...prev, url: e.target.value }))
                }
                placeholder="https://www.youtube.com/watch?v=..."
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "2px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <small
                style={{
                  color: "#666",
                  fontSize: "12px",
                  marginTop: "5px",
                  display: "block",
                }}
              >
                유효한 YouTube URL을 입력하세요
              </small>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                  color: "#2d3436",
                }}
              >
                YouTube 공개 여부
              </label>
              <select
                value={contentForm.youtubeVisibility}
                onChange={(e) =>
                  setContentForm((prev) => ({
                    ...prev,
                    youtubeVisibility: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "2px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              >
                <option value="unlisted">일부 공개 (제한된 임베드)</option>
                <option value="public">전체 공개 (YouTube Player)</option>
              </select>
              <small
                style={{
                  color: "#666",
                  fontSize: "12px",
                  marginTop: "5px",
                  display: "block",
                }}
              >
                일부 공개: iframe 임베드 방식 | 전체 공개: YouTube Player API 사용
              </small>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span style={{ fontWeight: "bold", color: "#2d3436" }}>
                  동영상 음성 활성화
                </span>
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: "50px",
                    height: "26px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={contentForm.enableVideoMusic}
                    onChange={(e) =>
                      setContentForm((prev) => ({
                        ...prev,
                        enableVideoMusic: e.target.checked,
                      }))
                    }
                    style={{
                      opacity: 0,
                      width: 0,
                      height: 0,
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: contentForm.enableVideoMusic
                        ? "#74b9ff"
                        : "#ccc",
                      transition: "0.4s",
                      borderRadius: "26px",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        content: '""',
                        height: "20px",
                        width: "20px",
                        left: contentForm.enableVideoMusic ? "27px" : "3px",
                        bottom: "3px",
                        backgroundColor: "white",
                        transition: "0.4s",
                        borderRadius: "50%",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      }}
                    />
                  </span>
                </label>
              </div>
              <small
                style={{
                  color: "#666",
                  fontSize: "12px",
                  marginTop: "5px",
                  display: "block",
                }}
              >
                활성화하면 동영상 재생 시 배경음악이 일시정지됩니다
              </small>
              {contentForm.videoType === "youtube" &&
                contentForm.youtubeVisibility === "unlisted" &&
                !contentForm.enableVideoMusic && (
                  <small
                    style={{
                      color: "#e17055",
                      fontSize: "12px",
                      marginTop: "8px",
                      display: "block",
                      backgroundColor: "#fff5f2",
                      padding: "8px 10px",
                      borderRadius: "4px",
                      border: "1px solid #ffd6cc",
                    }}
                  >
                    ℹ️ 일부공개 영상은 제약사항으로 인해 배경음악이 따로
                    재생되지 않습니다
                  </small>
                )}
            </div>
          </>
        )}

        {selectedContentType === "website" && (
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
                color: "#2d3436",
              }}
            >
              웹사이트 URL
            </label>
            <input
              type="url"
              value={contentForm.url}
              onChange={(e) =>
                setContentForm((prev) => ({ ...prev, url: e.target.value }))
              }
              placeholder="https://example.com"
              style={{
                width: "100%",
                padding: "10px",
                border: "2px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        background: "#f8f9fa",
        border: "2px dashed #dee2e6",
        borderRadius: "12px",
        padding: "20px",
        marginBottom: "20px",
      }}
    >
      {/* 섹션 헤더 */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          paddingBottom: "10px",
          borderBottom: "1px solid #dee2e6",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "20px" }}>📋</span>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "600",
              color: "#2d3436",
            }}
          >
            컨텐츠 관리
          </h3>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={handleAddContent}
            style={{
              background: "linear-gradient(45deg, #00b894, #00cec9)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 16px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            + 컨텐츠 추가
          </button>
        </div>
      </div>

      {/* 컨텐츠 목록 - 드래그 앤 드롭 적용 */}
      {localContentList && localContentList.length > 0 ? (
        <div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localContentList.map((item) => item.id.toString())}
              strategy={verticalListSortingStrategy}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {localContentList.map((mapping, index) => (
                  <SortableContentItem
                    key={mapping.id}
                    mapping={mapping}
                    index={index}
                    onEdit={handleEditContent}
                    onRemove={handleDeleteContent}
                    generateUrlForExistingContent={
                      generateUrlForExistingContent
                    }
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "40px 20px",
            textAlign: "center",
            border: "2px dashed #dee2e6",
          }}
        >
          <div style={{ fontSize: "3em", marginBottom: "15px", opacity: 0.6 }}>
            📋
          </div>
          <h4
            style={{
              margin: "0 0 8px 0",
              fontSize: "16px",
              color: "#495057",
              fontWeight: "600",
            }}
          >
            아직 추가된 컨텐츠가 없습니다
          </h4>
          <p style={{ margin: 0, fontSize: "14px", color: "#6c757d" }}>
            위의 "컨텐츠 추가" 버튼을 클릭해서 시작하세요!
          </p>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        message={`"${
          contentToDelete?.content?.title || "컨텐츠"
        }"를 정말로 삭제하시겠습니까?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isDelete={true}
      />

      {/* 컨텐츠 추가/수정 모달 */}
      {showAddModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "600px",
              width: "90%",
              maxHeight: "85vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
                paddingBottom: "10px",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#2d3436",
                }}
              >
                {isEditMode ? "컨텐츠 수정" : "컨텐츠 추가"}
              </h2>
              <button
                onClick={closeModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#636e72",
                }}
              >
                &times;
              </button>
            </div>

            {!selectedContentType ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: "15px",
                }}
              >
                <div
                  style={{
                    padding: "20px",
                    border: "2px solid #dee2e6",
                    borderRadius: "12px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => {
                    if (newFilePreviewUrl) {
                      URL.revokeObjectURL(newFilePreviewUrl);
                      setNewFilePreviewUrl(null);
                    }
                    setSelectedContentType("image");
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = "#74b9ff";
                    e.target.style.background = "#f8f9fa";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = "#dee2e6";
                    e.target.style.background = "white";
                  }}
                >
                  <div style={{ fontSize: "32px", marginBottom: "8px" }}>
                    🖼️
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#2d3436",
                      marginBottom: "4px",
                    }}
                  >
                    이미지
                  </div>
                  <div style={{ fontSize: "12px", color: "#636e72" }}>
                    JPG, PNG, GIF, SVG 등
                  </div>
                </div>
                <div
                  style={{
                    padding: "20px",
                    border: "2px solid #dee2e6",
                    borderRadius: "12px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => {
                    if (newFilePreviewUrl) {
                      URL.revokeObjectURL(newFilePreviewUrl);
                      setNewFilePreviewUrl(null);
                    }
                    setSelectedContentType("video");
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = "#74b9ff";
                    e.target.style.background = "#f8f9fa";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = "#dee2e6";
                    e.target.style.background = "white";
                  }}
                >
                  <div style={{ fontSize: "32px", marginBottom: "8px" }}>
                    🎬
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#2d3436",
                      marginBottom: "4px",
                    }}
                  >
                    동영상
                  </div>
                  <div style={{ fontSize: "12px", color: "#636e72" }}>
                    YouTube URL만 지원
                  </div>
                </div>
                <div
                  style={{
                    padding: "20px",
                    border: "2px solid #dee2e6",
                    borderRadius: "12px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => {
                    if (newFilePreviewUrl) {
                      URL.revokeObjectURL(newFilePreviewUrl);
                      setNewFilePreviewUrl(null);
                    }
                    setSelectedContentType("website");
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = "#74b9ff";
                    e.target.style.background = "#f8f9fa";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = "#dee2e6";
                    e.target.style.background = "white";
                  }}
                >
                  <div style={{ fontSize: "32px", marginBottom: "8px" }}>
                    🌐
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#2d3436",
                      marginBottom: "4px",
                    }}
                  >
                    웹사이트
                  </div>
                  <div style={{ fontSize: "12px", color: "#636e72" }}>
                    대시보드, 모니터링 등
                  </div>
                </div>
              </div>
            ) : (
              <>
                {renderContentForm()}
                <div
                  style={{
                    marginTop: "20px",
                    textAlign: "right",
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => {
                      if (newFilePreviewUrl) {
                        URL.revokeObjectURL(newFilePreviewUrl);
                        setNewFilePreviewUrl(null);
                      }
                      setSelectedContentType("");
                    }}
                    style={{
                      background: "#636e72",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "10px 16px",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    뒤로가기
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isUploading}
                    style={{
                      background: "linear-gradient(45deg, #74b9ff, #0984e3)",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "10px 20px",
                      fontSize: "14px",
                      cursor: isUploading ? "wait" : "pointer",
                      opacity: isUploading ? 0.7 : 1,
                    }}
                  >
                    {isUploading ? "처리 중..." : isEditMode ? "수정" : "추가"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// 서버 전송용 데이터 변환 함수들을 외부에서 사용할 수 있도록 노출
ContentManagement.convertContentListForServer = (contentList) => {
  return contentList.map((mapping, index) => {
    const content = mapping.content;

    const isNewItem =
      content?.isNewItem === true || // 명시적으로 새 아이템으로 표시된 경우
      !content?.id || // ID가 없는 경우
      (typeof content?.id === "string" &&
        content.id.toString().includes("temp_")) || // 임시 ID인 경우
      !mapping.mappingId; // 매핑 ID가 없는 경우 (새로 추가된 경우)

    const contentRequest = {
      contentType:
        content?.contentType || mapFrontendTypeToServer(content?.type),
      sortOrder: index + 1,
      playDuration: mapping.playDuration || 10,
      isMusicPlay: mapping.isMusicPlay || false,

      ...(content?.binaryData &&
        content.binaryData.length > 0 && {
          binaryData: content.binaryData,
        }),

      ...(content?.mimeType && {
        mimeType: content.mimeType,
      }),

      ...(!content?.binaryData &&
        content?.url && {
          url: content.url,
        }),

      ...(content?.videoType === "youtube" &&
        content?.youtubeVisibility && {
          youtubeVisibility: content.youtubeVisibility,
        }),

      ...(isNewItem
        ? {}
        : content?.id && {
            existingContentId: content.id,
          }),
    };

    const mappingRequest = {
      mappingId: mapping.mappingId || null,
      sortOrder: index + 1,
      playDuration: mapping.playDuration || 10,
      name: mapping.name || null,
      isMusicPlay: mapping.isMusicPlay || false,
      contentRequest: contentRequest,
    };

    return mappingRequest;
  });
};

export default ContentManagement;
